import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, MousePointerClick, Reply, Send, AlertTriangle, Loader2, Inbox as InboxIcon } from "lucide-react";
import { toast } from "sonner";
import { engageApi } from "@/lib/engageApi";
import { useI18n } from "@/lib/i18n";

const FILTERS = [
    { key: "reply", icon: Reply, cls: "text-brand-violet" },
    { key: "open", icon: Mail, cls: "text-emerald-300" },
    { key: "click", icon: MousePointerClick, cls: "text-brand-cyan" },
    { key: "sent", icon: Send, cls: "text-brand-indigo" },
    { key: "bounce", icon: AlertTriangle, cls: "text-rose-300" },
];

export default function Inbox() {
    const { t } = useI18n();
    const [type, setType] = useState("reply");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        engageApi.inbox(type, 100)
            .then(setItems)
            .catch(() => toast.error("Failed"))
            .finally(() => setLoading(false));
    }, [type]);

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{t.engage.inbox.title}</h1>
                <p className="mt-1 text-sm text-zinc-500">{t.engage.inbox.subtitle}</p>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setType(f.key)}
                        data-testid={`inbox-filter-${f.key}`}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                            type === f.key
                                ? "bg-brand-indigo text-white border-brand-indigo"
                                : "bg-white/[0.03] text-zinc-300 border-white/10 hover:border-white/25"
                        }`}
                    >
                        <f.icon size={12} />
                        {t.engage.inbox.filters[f.key]}
                    </button>
                ))}
            </div>

            <div className="rounded-2xl border border-white/8 bg-brand-surface overflow-hidden">
                {loading ? (
                    <div className="py-24 flex items-center justify-center text-zinc-500 text-sm">
                        <Loader2 size={18} className="animate-spin mr-2" /> Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-16 text-center">
                        <div className="mx-auto w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center mb-3">
                            <InboxIcon size={22} className="text-zinc-500" />
                        </div>
                        <div className="text-sm text-zinc-500" data-testid="inbox-empty">{t.engage.inbox.empty}</div>
                    </div>
                ) : (
                    <ul className="divide-y divide-white/5">
                        {items.map((e) => {
                            const cfg = FILTERS.find((f) => f.key === e.type) || FILTERS[0];
                            const Icon = cfg.icon;
                            return (
                                <motion.li
                                    key={e.event_id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="px-5 py-4 hover:bg-white/[0.03] transition"
                                    data-testid={`inbox-item-${e.event_id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 ${cfg.cls}`}>
                                            <Icon size={16} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-white font-medium">{e.lead_name}</span>
                                                <span className="text-xs text-zinc-500">·</span>
                                                <span className="text-xs text-zinc-400">{e.lead_company}</span>
                                            </div>
                                            <div className="text-sm text-zinc-300 mt-0.5 truncate">{e.subject}</div>
                                            {e.type === "reply" && (
                                                <div className="mt-1 text-sm text-brand-cyan italic">"{e.snippet}"</div>
                                            )}
                                            <div className="mt-1 text-[10px] font-mono text-zinc-600">{new Date(e.timestamp).toLocaleString()}</div>
                                        </div>
                                    </div>
                                </motion.li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
